import { AnimatePresence, motion } from "framer-motion";
import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect } from "react";
import styled from "styled-components";
import ImageEx from "./x-image";
import { CnIcon } from "@alife/cn-ui";
import { useI18n } from "@alife/panda-i18n";
import { useUpdateEffect } from "ahooks";
const sequence = {
    frameCount: 28,
    frameWidth: 128,
    frameHeight: 128,
    frameScale: 0.5,
    frameDirection: 'y',
    direction: 1,
};
const animation = {
    direction: 1,
    speed: 1.2,
    options: {
        autoplay: true,
        loop: false,
    },
};
const NoticeBalloon = motion.custom(styled.div `
  opacity: 1;
  transform: none;
  position: absolute;
  bottom: 30px;
  display: block;
  border-radius: 16px 16px 0 16px;
  width: max-content;
  max-height: 300px;
  overflow: auto;
  background-image: linear-gradient(35deg, #70b9ff 0%, #5287ff 100%);
  color: #fff;
  max-width: 500px;
  line-height: 1.7em;
  padding: 8px 12px;
  text-align: left;
  font-size: 13px;
  right: 64px;
`);
const Container = motion.custom(styled.div `
  position: fixed;
  bottom: 10px;
  right: 10px;
  width: 64px;
  height: 64px;
  cursor: pointer;
`);
const DragConstraints = styled.div `
  position: fixed;
  top: 0px;
  bottom: 0px;
  left: 0px;
  right: 74px;
  height: 100%;
  width: 100%;
  pointer-events: none;
`;
const PopupButton = motion.custom(styled.div `
  border-radius: 100%;
  width: 64px;
  height: 64px;
  display: block;
  color: #fff !important;
  text-align: center;
  line-height: 64px;
  cursor: pointer;
  position: absolute;
  top: 0;

  > div {
    width: 100%;
    height: 100%;

    img {
      width: 100%;
      height: 100%;
      -webkit-user-drag: none;
    }
  }
`);
const HiddenToggleButton = motion.custom(styled.a `
  position: absolute;
  width: 16px;
  height: 16px;
  line-height: 16px;
  border-radius: 8px;
  padding: 0;
  box-sizing: content-box !important;
  top: 0;
  right: -10px;
  background-color: #fff !important;
  color: #666 !important;
  cursor: pointer;
  &:hover {
    transition: background-color 0.3s;
    color: #0567fa !important;
  }
`);
const variants = {
    active: {
        opacity: 1,
        scale: 1,
        x: 0,
        right: 10,
        transition: { duration: 0.3 },
    },
    activeSnapEdge: {
        opacity: 1,
        scale: 1,
        x: 0,
        right: -30,
        transition: { duration: 0.3 },
    },
    inactive: {
        opacity: 1,
        scale: 1,
        x: 0,
        right: 10,
        transition: { duration: 0.3 },
        translateX: 0
    }
};
const buttonVariants = {
    active: {
        rotate: 0,
        transition: { duration: 0.3 },
    },
    inactive: {
        rotate: -30,
        transition: { duration: 0.3 },
    },
};
export default forwardRef(function MainMenuButton(props, ref) {
    const { visible } = props;
    const $i18n = useI18n();
    const dragConstraintsRef = useRef(null);
    const dragTargetRef = useRef(null);
    const entryRef = useRef(null);
    const [dragging, setDragging] = useState(false);
    // 辅助 面板打开时拖拽之后回到最右边
    const [dragged, setDragged] = useState(false);
    const [hover, setHover] = useState(false);
    useImperativeHandle(ref, () => ({
        getPosition() {
            return entryRef.current;
        },
        getBoundingClientRect() {
            return dragTargetRef.current?.getBoundingClientRect?.();
        }
    }));
    const [showNotice, setShowNotice] = useState(false);
    const [isSnapEdge, setIsSnapEdge] = useState(false);
    const [mirrored, setMirrored] = useState(false);
    const calcIsMirror = () => {
        const el = dragTargetRef.current;
        const containerEl = dragConstraintsRef.current;
        if (!el || !containerEl)
            return;
        const translateX = +el.style.transform.replace(/translateX\((-?[\d.]+)px\).*/g, "$1");
        const offset = containerEl.offsetWidth + translateX - el.offsetWidth / 2;
        const curMirrored = offset < containerEl.offsetWidth / 2;
        setMirrored(curMirrored);
    };
    const timerFlag = useRef();
    const autoHideEntryRef = useRef(props.autoHideEntry);
    const foldEntry = () => {
        setIsSnapEdge(true);
        setShowNotice(false);
    };
    useEffect(() => {
        if (autoHideEntryRef.current) {
            setTimeout(foldEntry, 3000);
        }
    }, []);
    useUpdateEffect(() => {
        // 面板关闭后自定收起
        if (!visible) {
            foldEntry();
        }
    }, [visible]);
    return (React.createElement(React.Fragment, null,
        React.createElement(DragConstraints, { ref: dragConstraintsRef }),
        React.createElement(Container, { ref: dragTargetRef, onDragStart: () => {
                setDragging(true);
                setDragged(false);
                setShowNotice(false);
                setHover(false);
            }, onDragEnd: () => {
                setTimeout(() => {
                    setDragging(false);
                }, 300);
            }, style: {
                y: props.position?.y,
                ...(!props.showEntry ? { width: 'auto', height: 'auto' } : {})
            }, onMouseEnter: () => {
                if (timerFlag.current) {
                    clearTimeout(timerFlag.current);
                    timerFlag.current = null;
                }
                setIsSnapEdge(false);
            }, onMouseLeave: () => {
                if (!props.showEntry)
                    return;
                // 非拖拽和 面板展示的情况
                if (!dragging && !visible) {
                    setIsSnapEdge(true);
                    setShowNotice(false);
                }
            }, onDrag: () => {
                calcIsMirror();
                entryRef.current && props?.onDrag?.(entryRef.current);
            }, transformTemplate: ({ x, y }) => {
                if (x == null || y == null) {
                    entryRef.current = null;
                    if (props.position) {
                        entryRef.current = props.position;
                    }
                }
                entryRef.current = {
                    x: parseInt(x, 10),
                    y: parseInt(y, 10),
                };
                // console.log('entryRef.current', entryRef.current);
                return ` translateX(${x || 0}) translateY(${y || 0})`;
            }, onDragTransitionEnd: () => {
                calcIsMirror();
                setDragged(true);
                entryRef.current && props?.onDrag?.(entryRef.current);
            }, dragConstraints: dragConstraintsRef, dragTransition: { timeConstant: 100 }, positionTransition: false, dragging: false, drag: true, variants: variants, animate: visible && dragged ? 'inactive' : !visible && isSnapEdge ? "activeSnapEdge" : "active" },
            !isSnapEdge && (dragging ? React.createElement("div", { onClickCapture: (e) => e.preventDefault() }, props.children) : props.children),
            !!props.showEntry && React.createElement(AnimatePresence, { initial: false },
                React.createElement(PopupButton, { "data-mirrored": mirrored, onMouseEnter: () => {
                        setHover(true);
                        setShowNotice(true);
                    }, onMouseLeave: () => {
                        setHover(false);
                        setShowNotice(false);
                    }, onClick: () => {
                        setShowNotice(false);
                        setHover(false);
                        setIsSnapEdge(false);
                        if (dragging) {
                            return;
                        }
                        props.onClick?.();
                    }, initial: { opacity: 1, scale: 1 }, exit: {
                        opacity: 0,
                        scale: 0.1,
                        transition: { duration: 0.3 },
                    }, variants: buttonVariants, animate: !props.visible && isSnapEdge ? "inactive" : "active" },
                    React.createElement("div", null,
                        React.createElement(ImageEx, { src: 'https://img.alicdn.com/imgextra/i2/O1CN01uCAi5Y1aaVuPqCLCr_!!6000000003346-2-tps-128-3584.png', sequence: {
                                ...sequence,
                                direction: showNotice ? 1 : -1,
                            }, animation: {
                                ...animation,
                                direction: showNotice ? 1 : -1,
                            } })))),
            React.createElement(AnimatePresence, { initial: false }, !visible && hover && (React.createElement(HiddenToggleButton, { onMouseEnter: () => {
                    setHover(true);
                }, onMouseLeave: () => {
                    setHover(false);
                }, onClick: (e) => {
                    e.stopPropagation();
                    setIsSnapEdge(true);
                    setShowNotice(false);
                }, initial: { opacity: 0 }, animate: { opacity: 1 }, exit: {
                    opacity: 0,
                    transition: { duration: 0.2, delay: 0.3 },
                } },
                React.createElement(CnIcon, { type: "minus-r-fill", size: "medium" })))),
            React.createElement(AnimatePresence, { initial: false }, !visible && showNotice && (React.createElement(NoticeBalloon, { initial: { opacity: 0, scale: 0.1, x: 10 }, animate: { opacity: 1, scale: 1, x: 0 }, exit: {
                    opacity: 0,
                    scale: 0.1,
                    x: 10,
                    transition: { duration: 0.2, delay: 0.3 },
                }, onClick: (e) => {
                    e.stopPropagation();
                } },
                React.createElement("div", { style: { maxWidth: 175 } }, $i18n.get({
                    id: 'IfYouHaveAnyQuestionsPlease_66071262.ued',
                    dm: ' 有问题可以找我哦~',
                }))))))));
});
